import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { Modal } from '../../shared/ui/modal';
import { MutationError } from '../../shared/ui/mutation-error';
import { Note } from '../../shared/ui/note';
import {
  SCOPE_ROOT_LABEL,
  countLine,
  deleteFolder,
  folderKeys,
  type TFolder,
} from '../../entities/folder';

interface IFolderDeleteProps {
  folder: TFolder;
  noun: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted?: (folder: TFolder) => void;
}

// The server's rule, said before the click: a folder goes only when empty, and a non-empty
// one is emptied one level up first — nothing filed in it is ever deleted.
export const FolderDelete = ({
  folder,
  noun,
  open,
  onOpenChange,
  onDeleted,
}: IFolderDeleteProps) => {
  const client = useQueryClient();
  const [error, setError] = useState<unknown>(null);
  const [waiting, setWaiting] = useState(false);
  const empty = folder.itemCount === 0 && folder.folderCount === 0;
  const parentName =
    folder.path.length > 1
      ? folder.path[folder.path.length - 2].name
      : SCOPE_ROOT_LABEL[folder.scope];

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const confirm = async () => {
    setWaiting(true);
    setError(null);
    try {
      await deleteFolder(folder.id, !empty);
      await Promise.all([
        client.invalidateQueries({ queryKey: folderKeys.list(folder.scope) }),
        client.invalidateQueries({ queryKey: [folder.scope === 'flows' ? 'flows' : 'library'] }),
      ]);
      onOpenChange(false);
      onDeleted?.(folder);
    } catch (caught) {
      setError(caught);
    } finally {
      setWaiting(false);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      width="md"
      title={`Delete “${folder.name}”`}
      description={
        empty
          ? 'The folder is empty.'
          : `It holds ${countLine(folder, noun)}. A folder is deleted only when it is empty.`
      }
      submit={{
        label: empty ? 'Delete folder' : `Move them to ${parentName} and delete`,
        onSubmit: () => void confirm(),
        waiting,
        waitingLabel: 'Deleting',
      }}
    >
      <div className="flex flex-col gap-4 pt-2">
        {!empty && (
          <Note>
            What is in it moves one level up, into {parentName}, and keeps its own folders. No{' '}
            {noun} is deleted.
          </Note>
        )}
        <MutationError error={error} />
      </div>
    </Modal>
  );
};
